<template>
  <v-card class="step-tab-panel" flat>
    <div class="assortment-table d-flex flex-column">
      <v-container class="actions-container pa-2 ma-0 flex-grow-0">
        <v-row class="pa-0 ma-0">
          <v-col class="d-flex align-center flex-grow-1">
            <span class="mr-3 font-weight-bold">
              {{ tkey('setList') }}

              <!-- StoreClass definition tooltip -->
              <DocsLink link="toolguide/04a-storeclasses.html" />
            </span>

            <v-btn
              v-if="showNotImplemented"
              data-id-e2e="btnStoreClassImport"
              class="button-space-5"
              primary
              disabled
            >
              {{ $t('actions.manualImport') }}
              <v-icon end> $import </v-icon>
            </v-btn>
            <AdvancedFilters
              :filters="filters"
              :filter-options="filterOptions"
              :btn-text="filterButtonText"
              @change="handleFilterChange"
              @apply="filterFurnitureWithStoreClass"
            />
          </v-col>
          <v-col
            v-if="showErrorControls"
            id="error-controls"
            class="d-flex align-center col flex-grow-2"
          >
            <span class="invalid-rows-error-box pr-2">{{ invalidRowsErrorMessage }}</span>
            <v-btn
              v-if="!filterInvalidRows"
              variant="outlined"
              color="error"
              @click="toggleInvalidRows"
            >
              {{ $t('actions.showInvalidRows') }}
            </v-btn>
            <v-btn v-else variant="outlined" color="primary" @click="toggleInvalidRows">
              {{ $t('actions.showAllRows') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
      <v-container
        class="actions-container dynamic-height-container storeclass-container d-flex flex-column pa-0 pt-2 flex-grow-1"
      >
        <v-row no-gutters class="mt-2 mb-1 flex-grow-0">
          <v-col cols="2">
            <v-card-text class="list-title">
              <div>{{ tkey('storeClassList') }}</div>
            </v-card-text>
          </v-col>
        </v-row>
        <div class="storeclass-list-container">
          <v-col cols="2" class="pt-0 storeclass-list-column">
            <v-divider class="storeclass-border mb-2" />
            <div class="padded-column">
              <div class="storeclass-list-scrollable">
                <v-form ref="storeClassNamesForm" v-model="valid" @submit.prevent>
                  <v-card
                    v-for="storeClass in storeClasses"
                    :key="storeClass._id"
                    class="mb-1 storeclass-card"
                    flat
                    primary
                  >
                    <v-row class="align-items-center">
                      <v-col class="storeclass-col">
                        <AssortmentTextField
                          :ref="`storeClassField_${storeClass._id}`"
                          :key="nameKey + storeClass._id"
                          v-model="storeClass.name"
                          class="storeclass-text-field"
                          :rules="storeClassInputRules"
                          :disabled="editingDisabled"
                          variant="underlined"
                          run-validations-on-creation
                          @update:model-value="[revalidateForm(), redrawRows()]"
                        />
                        <!-- have to call redrawRows for all ag-grids so storeclass names stay in sync. -->
                        <!-- note: this is not performant. AOV3-754 TODO: only refresh tables when storeclass has furniture. only refresh related rows. -->
                      </v-col>
                      <v-col cols="2" class="mx-0 my-auto pa-0 d-flex justify-end">
                        <v-icon
                          :disabled="editingDisabled"
                          size="19"
                          class="small-icon mr-1"
                          @click="removeStoreClass(storeClass._id)"
                        >
                          $trash
                        </v-icon>
                      </v-col>
                    </v-row>
                  </v-card>
                </v-form>
              </div>
              <v-btn
                :disabled="editingDisabled"
                data-id-e2e="btnCreateStoreClass"
                primary
                variant="flat"
                class="ml-2 mb-2 mt-2 storeclass-btn"
                @click="() => addNewStoreClass(false)"
              >
                {{ tkey('newStoreClass') }}
              </v-btn>
            </div>
            <v-divider class="storeclass-border" />
            <div class="padded-column">
              <v-btn
                :disabled="editingDisabled"
                variant="outlined"
                secondary
                class="ml-2 mb-2 mt-2 single-storeclass-btn storeclass-btn outlined-btn"
                @click="onSingleStoreClassClick"
              >
                {{ tkey('singleStoreClass') }}
              </v-btn>
            </div>
          </v-col>
          <v-col cols="10" class="furniture-col-scrollable pt-0 pl-0">
            <v-col cols="7" class="pl-2 pt-2">
              <h3 v-if="filters.length && !filteredFurnitureCategorySets.length">
                {{ $t('errors.noDataAvailableForFilters') }}
              </h3>
              <div
                v-for="(furnitureSet, index) in filteredFurnitureCategorySets"
                :key="`${furnitureSet + index}-container`"
                class="d-flex flex-column storeclass-table h-100 w-100"
              >
                <b class="section">{{ furnitureSet }}</b>
                <AgGridVue
                  :id="`ag-grid-${furnitureSet}`"
                  dom-layout="autoHeight"
                  class="h-100 w-100"
                  :column-defs="columnDefs"
                  :row-data="groupedFurniture[furnitureSet]"
                  :grid-options="gridOptions[furnitureSet]"
                  :does-external-filter-pass="doesExternalFilterPass"
                  @cell-value-changed="onSelectionChange"
                  @grid-ready="onGridReady($event, furnitureSet)"
                  @row-clicked="modifySelectPosition(`#ag-grid-${furnitureSet}`)"
                />
              </div>
            </v-col>
          </v-col>
        </div>
      </v-container>
      <PageActions
        :show-export="showNotImplemented"
        :has-data-changes="hasDataChanges"
        :has-data-errors="!valid"
        :save-disabled="editingDisabled"
        @discard="discardChanges"
        @save="saveChanges"
      />
    </div>

    <v-dialog
      v-model="isDeleteDialogOpen"
      retain-focus
      max-width="450"
      content-class="rtls-dialog"
      @keydown.esc="closeConfirmDialog"
      @click:outside="closeConfirmDialog"
    >
      <BaseDialogCard
        ref="deleteDialog"
        :title="tkey('deleteStoreclassTitle')"
        :border="true"
        @close="closeConfirmDialog"
      >
        <v-card-text class="text-h6">
          <p class="pb-2">{{ tkey('changingStoreClassesWarning') }}</p>
        </v-card-text>

        <template #footer>
          <v-card-actions>
            <v-btn primary @click="closeConfirmDialog">
              {{ $t('actions.cancel') }}
            </v-btn>
            <v-btn secondary variant="flat" class="outlined-btn" @click="onDeleteConfirm">
              {{ tkey('removeStoreClass') }}
            </v-btn>
          </v-card-actions>
        </template>
      </BaseDialogCard>
    </v-dialog>

    <v-dialog
      v-model="isSingleStoreClassDialogOpen"
      retain-focus
      max-width="450"
      content-class="rtls-dialog"
      @keydown.esc="closeConfirmDialog"
      @click:outside="closeConfirmDialog"
    >
      <BaseDialogCard
        ref="deleteDialog"
        :title="tkey('useSingleStoreclassTitle')"
        :border="true"
        @close="closeConfirmDialog"
      >
        <v-card-text class="text-h6">
          <p class="pb-2">{{ tkey('removeAllWarning') }}</p>
        </v-card-text>

        <template #footer>
          <v-card-actions>
            <v-btn primary @click="closeConfirmDialog">
              {{ $t('actions.cancel') }}
            </v-btn>
            <v-btn secondary variant="flat" class="outlined-btn" @click="replaceStoreClassToSingle">
              {{ $t('general.yes') }}
            </v-btn>
          </v-card-actions>
        </template>
      </BaseDialogCard>
    </v-dialog>

    <DependencyTreeFeedbackModal
      :value="dependencyTreeModalOpen"
      :results="dependencyTreeFeedback"
      page="storeclassDefinition"
      @close="closeDependencyTreeModal"
      @commit="saveChanges(true)"
    />

    <UnsavedDataModal
      ref="unsavedDataModal"
      :value="isUnsavedDataModalOpen"
      @cancel="closeUnsavedDataModal"
      @confirm="confirmUnsavedDataModal"
    />
  </v-card>
</template>

<script>
import { useInputValidations } from '@/composables/input-validations';
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useFeature } from '@/composables/use-feature';
import { useScenarioStatusFormatter } from '@/composables/use-scenario-status-formatter';
import { useAiAgentActions } from '@/composables/use-ai-agent-actions';
import { useContextStore } from '@/stores/context';
import { useAiStore } from '@/stores/ai';
import { useDependencyTreeStore } from '@/stores/dependency-tree';
import { useFurnitureStore } from '@/stores/furniture';
import { useScenariosStore } from '@/stores/scenarios';
import agGridUtils from '@/utils/ag-grid-utils';
import filterUtils from '@/utils/filter-utils';
import {
  cloneDeep,
  differenceBy,
  each,
  extend,
  get,
  groupBy,
  isEmpty,
  isEqual,
  keyBy,
  keys,
  map,
  mapValues,
  reduce,
  startsWith,
  uniq,
  uniqueId,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { getCurrentInstance } from 'vue';

const newlyAddedStoreClassIdPrefix = 'newlyAddedStoreClass';

export default {
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeave(to, from, next);
  },
  setup() {
    const instance = getCurrentInstance();
    const { canEditStoreclassDefinitionPage } = usePermission();
    const { tkey } = useInternationalization('storeClassDefinitionPage');
    const { aiPanelEnabled } = useFeature();
    const { formatScenarioProgress } = useScenarioStatusFormatter('storeclass-definition');

    useAiAgentActions({
      allowScenarioWorkflowNavigation: true,
      getExclude: () =>
        instance.proxy.editingDisabled
          ? [
              'create-store-class',
              'rename-store-class',
              'delete-store-class',
              'assign-furniture-to-store-class',
              'use-single-store-class',
              'save-store-classes',
              'discard-store-class-changes',
            ]
          : [],
      getSupplementary: () => ({
        'assign-furniture-to-store-class': instance.proxy.buildFurnitureAssignmentContext(),
      }),
      actions: {
        'create-store-class': {
          description:
            'Create a new store class with the given name. The name must be unique across all existing store classes. ' +
            'After creating, you can assign furniture archetypes to it using assign-furniture-to-store-class. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Creating store class',
          confirmationLabel: args => `create store class "${args.name}"`,
          parameters: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'Unique name for the new store class.' },
            },
            required: ['name'],
          },
          handler: async ({ name }) => {
            if (instance.proxy.editingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            if (!name || !name.trim())
              return '[Tool] Action failed: name is required. Ask the user to provide a name.';
            const trimmedName = name.trim();
            const duplicate = instance.proxy.storeClasses.find(
              sc => sc.name.trim() === trimmedName
            );
            if (duplicate)
              return `[Tool] Action failed: a store class named "${trimmedName}" already exists. Store class names must be unique. Ask the user to choose a different name.`;
            instance.proxy.addNewStoreClass(false);
            const newClass = instance.proxy.storeClasses[instance.proxy.storeClasses.length - 1];
            newClass.name = trimmedName;
            await instance.proxy.revalidateForm();
            return `[Tool] Action succeeded: created store class "${trimmedName}".`;
          },
        },

        'rename-store-class': {
          description:
            'Rename an existing store class. Use the current name exactly as shown in the store class list. The new name must be unique. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Renaming store class',
          confirmationLabel: args =>
            `rename store class "${args.currentName}" to "${args.newName}"`,
          parameters: {
            type: 'object',
            properties: {
              currentName: {
                type: 'string',
                description: 'Exact current name of the store class.',
              },
              newName: { type: 'string', description: 'New unique name for the store class.' },
            },
            required: ['currentName', 'newName'],
          },
          handler: async ({ currentName, newName }) => {
            if (instance.proxy.editingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            if (!currentName || !newName)
              return '[Tool] Action failed: both currentName and newName are required. Ask the user to clarify.';
            const newTrimmed = newName.trim();
            const storeClass = instance.proxy.storeClasses.find(sc => sc.name === currentName);
            if (!storeClass)
              return `[Tool] Action failed: store class "${currentName}" not found. Ask the user to clarify the name.`;
            const duplicate = instance.proxy.storeClasses.find(
              sc => sc.name.trim() === newTrimmed && sc._id !== storeClass._id
            );
            if (duplicate)
              return `[Tool] Action failed: a store class named "${newTrimmed}" already exists. Store class names must be unique. Ask the user to choose a different name.`;
            storeClass.name = newTrimmed;
            await instance.proxy.revalidateForm();
            return `[Tool] Action succeeded: renamed store class "${currentName}" to "${newTrimmed}".`;
          },
        },

        'delete-store-class': {
          description:
            'Delete a store class. Any furniture archetypes assigned to it will become unassigned and must be reassigned before saving. ' +
            'Use the exact name as shown in the store class list. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Deleting store class',
          confirmationLabel: args => `delete store class "${args.name}"`,
          parameters: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'Exact name of the store class to delete.' },
            },
            required: ['name'],
          },
          handler: async ({ name }) => {
            if (instance.proxy.editingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            const storeClass = instance.proxy.storeClasses.find(sc => sc.name === name);
            if (!storeClass)
              return `[Tool] Action failed: store class "${name}" not found. Ask the user to clarify the name.`;
            // Bypass the confirmation dialog — set internal state and call onDeleteConfirm directly
            instance.proxy.storeClassToDelete = storeClass._id;
            instance.proxy.deleteAllStoreClasses = false;
            instance.proxy.onDeleteConfirm();
            const stillExists = instance.proxy.storeClasses.find(sc => sc._id === storeClass._id);
            if (stillExists)
              return `[Tool] Action failed: store class "${name}" could not be deleted. Inform the user.`;
            return `[Tool] Action succeeded: deleted store class "${name}". Any furniture previously assigned to it is now unassigned and needs to be reassigned before saving.`;
          },
        },

        'assign-furniture-to-store-class': {
          description:
            'Assign a furniture archetype to a store class. Use exact names as shown in the supplementary context. ' +
            'Each furniture archetype can only belong to one store class at a time — assigning it to a new class removes it from its current one. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Assigning furniture to store class',
          confirmationLabel: args =>
            `assign "${args.furnitureName}" to store class "${args.storeClassName}"`,
          parameters: {
            type: 'object',
            properties: {
              furnitureName: {
                type: 'string',
                description: 'Exact name of the furniture archetype to assign.',
              },
              storeClassName: {
                type: 'string',
                description: 'Exact name of the store class to assign the furniture to.',
              },
            },
            required: ['furnitureName', 'storeClassName'],
          },
          handler: async ({ furnitureName, storeClassName }) => {
            if (instance.proxy.editingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            const furniture = instance.proxy.furnituresWithStoreClass.find(
              f => f.furnitureName === furnitureName
            );
            if (!furniture)
              return `[Tool] Action failed: furniture archetype "${furnitureName}" not found. Check the supplementary context for valid furniture names and ask the user to clarify.`;
            const storeClass = instance.proxy.storeClasses.find(sc => sc.name === storeClassName);
            if (!storeClass)
              return `[Tool] Action failed: store class "${storeClassName}" not found. Ask the user to clarify the name.`;
            instance.proxy.addFurnitureToStoreClass(storeClass, furniture.furnitureId);
            instance.proxy.setupFurnituresWithStoreClasses();
            await instance.proxy.revalidateForm();
            return `[Tool] Action succeeded: assigned "${furnitureName}" to store class "${storeClassName}".`;
          },
        },

        'use-single-store-class': {
          description:
            'Replace all existing store classes with a single store class and assign every furniture archetype to it. ' +
            'This is a destructive operation — all current store classes are removed and replaced with one. ' +
            'Use when the user wants a simple single-canvas setup without separate assortments per fixture type. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Switching to single store class',
          confirmationLabel: args =>
            args.name
              ? `replace all store classes with a single class named "${args.name}"`
              : 'replace all store classes with a single store class',
          parameters: {
            type: 'object',
            properties: {
              name: {
                type: 'string',
                description:
                  'Optional. Name for the single store class. Defaults to "All Furniture Archetypes" if not provided.',
              },
            },
            required: [],
          },
          handler: async ({ name } = {}) => {
            if (instance.proxy.editingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            instance.proxy.replaceStoreClassToSingle();
            if (name && name.trim()) {
              const singleClass = instance.proxy.storeClasses[0];
              if (singleClass) {
                singleClass.name = name.trim();
                await instance.proxy.revalidateForm();
              }
            }
            const totalFurniture = instance.proxy.furnituresWithStoreClass.length;
            const assignedFurniture = instance.proxy.furnituresWithStoreClass.filter(
              f => f.storeClassId
            ).length;
            const singleClassName =
              instance.proxy.storeClasses[0]?.name || 'All Furniture Archetypes';
            return `[Tool] Action succeeded: replaced all store classes with a single store class named "${singleClassName}". ${assignedFurniture}/${totalFurniture} furniture archetypes are now assigned to it.`;
          },
        },

        'save-store-classes': {
          description:
            'Save all current store class changes (store class names and furniture assignments) to the database. ' +
            'IMPORTANT: Do NOT save automatically after individual operations like create, rename, delete, or assign. ' +
            'Only call this tool when: (1) the user explicitly asks to save, ' +
            '(2) the user uses language like "go ahead", "apply", "save it", or "just do it", ' +
            'or (3) you have completed ALL steps of an explicitly user-approved multi-step plan and saving was implied. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Saving store classes',
          confirmationLabel: 'save all store class changes',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            if (instance.proxy.editingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            if (!instance.proxy.valid)
              return '[Tool] Action failed: the form has validation errors (e.g. empty or duplicate store class names, or unassigned furniture archetypes). Ask the user to resolve validation errors before saving.';
            if (!instance.proxy.hasDataChanges)
              return '[Tool] Action failed: there are no unsaved changes to save. Inform the user.';
            await instance.proxy.saveChanges();
            if (instance.proxy.dependencyTreeModalOpen)
              return '[Tool] Action succeeded with required user input: saving these store class changes will affect downstream work (e.g. spacebreaks, assortment canvases). A confirmation modal is now open listing what will be reset. The user must review the modal and either confirm or cancel before continuing.';
            if (instance.proxy.hasDataChanges)
              return '[Tool] Action failed: the save did not complete. Inform the user and suggest they try again or refresh the page.';
            return '[Tool] Action succeeded: store class changes saved.';
          },
        },

        'discard-store-class-changes': {
          description:
            'Discard all unsaved store class changes, reverting to the last saved state. ' +
            'Only call this when the user explicitly asks to discard, undo, revert, or cancel their changes. ' +
            'Do not call this proactively. ' +
            'Do not confirm success in your message text; the system will confirm or report errors after the action completes.',
          workingLabel: 'Discarding changes',
          confirmationLabel: 'discard all unsaved store class changes',
          parameters: { type: 'object', properties: {}, required: [] },
          handler: async () => {
            if (instance.proxy.editingDisabled)
              return '[Tool] Action failed: editing is disabled on this page. Inform the user.';
            if (!instance.proxy.hasDataChanges)
              return '[Tool] Action failed: there are no unsaved changes to discard. Inform the user.';
            instance.proxy.discardChanges();
            if (instance.proxy.hasDataChanges)
              return '[Tool] Action failed: the discard did not fully revert the changes. Inform the user and suggest they refresh the page.';
            return '[Tool] Action succeeded: all unsaved store class changes have been discarded.';
          },
        },
      },
    });

    const {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
    } = useUnsavedDataWarning();

    const {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    } = useInputValidations();

    return {
      canEditStoreclassDefinitionPage,
      tkey,
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
      aiPanelEnabled,
      formatScenarioProgress,
    };
  },

  data() {
    return {
      errorData: {},
      filterInvalidRows: false,
      gridApis: {},
      storeClasses: [],
      storeClassInputRules: [this.required, this.isUniqueStoreClassName, this.hasFurniture],
      nameKey: false,
      valid: true,
      isDeleteDialogOpen: false,
      isSingleStoreClassDialogOpen: false,
      confirmMessages: [],
      deleteAllStoreClasses: false, // used to toggle contents of deletion modal
      gridOptions: {},
      filters: [],
      furnituresWithStoreClass: [],
      filteredFurnituresWithStoreClass: [],
      dependencyTreeFeedback: {},
      dependencyTreeModalOpen: false,
    };
  },
  computed: {
    ...mapState(useAiStore, ['isSidePanelOpen']),
    ...mapState(useFurnitureStore, ['getFurnituresInScenario', 'getStoreClassesInScenario']),
    ...mapState(useContextStore, ['showNotImplemented']),
    ...mapState(useScenariosStore, ['selectedScenario']),

    editingDisabled() {
      return (
        !this.canEditStoreclassDefinitionPage ||
        !get(this.selectedScenario, 'status.space.canBeEdited', true)
      );
    },

    groupedFurniture() {
      return groupBy(this.filteredFurnituresWithStoreClass, 'furnitureCategory1Key');
    },

    // These are all the options users will have to use in the filters component
    filterOptions() {
      return [
        {
          type: 'furnitureArchetypeKey',
          path: 'furnitureCategory1Key',
          allowSelectMultiple: true,
          name: this.tkey('furnitureArchetypeSet'),
          values: this.furnitureCategorySets.map(catSet => ({ name: catSet, value: catSet })),
        },
        {
          type: 'furnitureName',
          path: 'furnitureName',
          allowSelectMultiple: true,
          name: this.tkey('furnitureName'),
          values: this.getFurnituresInScenario.map(furniture => ({
            name: furniture.name,
            value: furniture.name,
          })),
        },
        {
          type: 'storeClass',
          path: 'storeClassId',
          allowSelectMultiple: true,
          name: this.tkey('storeClass'),
          values: this.storeClasses.map(storeClass => ({
            name: storeClass.name,
            value: storeClass._id,
          })),
        },
      ];
    },

    filterButtonText() {
      const originalFurnitureLength = this.furnituresWithStoreClass.length;
      const filteredFurnitureLength = this.filteredFurnituresWithStoreClass.length;
      return `${this.$t(
        'storeClassDefinitionPage.filters',
        this.filters.length
      )} - (${filteredFurnitureLength}/${originalFurnitureLength}) ${this.tkey('furniture')}`;
    },

    furnitureCategorySets() {
      return uniq(this.getFurnituresInScenario.map(f => f.furnitureCategory1Key)).sort();
    },

    filteredFurnitureCategorySets() {
      return uniq(this.filteredFurnituresWithStoreClass.map(f => f.furnitureCategory1Key)).sort();
    },

    hasDataChanges() {
      return (
        this.getStoreClassesInScenario.length !== this.storeClasses.length ||
        differenceBy(this.storeClasses, this.getStoreClassesInScenario, 'name').length > 0 ||
        this.hasMappingChanges
      );
    },

    hasMappingChanges() {
      // Checks whether our mappings of furniture archetypes to storeclasses are different
      // from what our initial state was. It breaks once it finds a single difference.
      const originalFurnitureIdToStoreClassMap = this.getStoreClassesInScenario.reduce(
        (m, storeClass) => {
          return {
            ...m,
            ...storeClass.furnitureIds.reduce((acc, id) => ({ [id]: storeClass._id, ...acc }), {}),
          };
        },
        {}
      );
      return !isEqual(
        originalFurnitureIdToStoreClassMap,
        mapValues(this.furnitureIdToStoreClassMap, v => v._id)
      );
    },

    furnitureIdToStoreClassMap() {
      return this.storeClasses.reduce((m, storeClass) => {
        return {
          ...m,
          ...storeClass.furnitureIds.reduce((acc, id) => ({ [id]: storeClass, ...acc }), {}),
        };
      }, {});
    },

    storeClassById() {
      return keyBy(this.storeClasses, '_id');
    },

    storeClassByNameAndId() {
      return extend(
        mapValues(keyBy(this.storeClasses, 'name'), '_id'),
        mapValues(this.storeClassById, '_id')
      );
    },

    columnDefs() {
      return [
        {
          headerName: '',
          field: 'furnitureName',
          colId: 'furnitureName',
          editable: false,
        },
        {
          headerName: '',
          field: 'storeClassId',
          colId: 'storeClassId',
          width: 240,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: {
            values: map(this.storeClasses, '_id'),
            field: 'storeClassId',
          },
          valueFormatter: this.formatStoreclassName,
          editable: !this.editingDisabled,
          filterParams: { valueFormatter: null },
          tooltipValueGetter(params) {
            return params.valueFormatted;
          },
        },
      ];
    },

    showErrorControls() {
      return !isEmpty(this.errorData) || this.filterInvalidRows;
    },

    invalidRowsErrorMessage() {
      return this.$t('validationErrors.rowsHaveInvalidValues');
    },
  },

  created() {
    this.storeClasses = cloneDeep(this.getStoreClassesInScenario);
    this.setupFurnituresWithStoreClasses();

    // each grid must have its own config, so generate one keyed by set key
    this.gridOptions = reduce(
      keys(this.groupedFurniture),
      (acc, archetypeSetKey) => {
        return {
          ...acc,
          [archetypeSetKey]: {
            columnMenu: 'legacy',
            suppressContextMenu: true,
            headerHeight: 0,
            stopEditingWhenCellsLoseFocus: true,
            tooltipShowDelay: 0,
            cellSelection: { handle: { mode: 'range' } },
            defaultColDef: {
              filter: false,
              sortable: false,
              editable: true,
              menuTabs: [],
              suppressMovable: true,
              minWidth: 100,
              flex: 1,
              suppressKeyboardEvent: agGridUtils.utils.suppressKeyboardEvent,
            },
            isExternalFilterPresent() {
              return true;
            },
            processDataFromClipboard: agGridUtils.utils.processDataFromClipboard,
            processCellFromClipboard: this.processCellFromClipboard,
          },
        };
      },
      {}
    );
    this.validateFurnitureCategories();

    this.$nextTick(() => {
      const aiStore = useAiStore();
      aiStore.enableSidePanel({
        section: 'storeclass-definition',
        contextDisplay: [this.selectedScenario?.name].filter(Boolean),
      });
      aiStore.setSidePanelContextFactory(() => this.buildInnerContext());
      // Safe to set immediately: ScenarioWorkflow + ScenarioSpace v-if gates ensure
      // selectedScenario and furniture store data are both populated before this page mounts.
      aiStore.setContextReady(true);
    });
  },

  methods: {
    ...mapActions(useFurnitureStore, ['saveScenarioFurniture']),
    ...mapActions(useDependencyTreeStore, ['triggerDependencyTree']),
    ...mapActions(useScenariosStore, ['refreshScenario']),

    validateFurnitureCategories() {
      this.furnitureCategorySets.forEach(fs => {
        this.checkMultipleRowsForErrors(this.groupedFurniture[fs]);
      });
    },

    async revalidateForm() {
      // Wait for all field refs to become available in the DOM before revalidating
      await this.$nextTick();

      // Trigger validation for each AssortmentTextField component
      this.revalidateStoreClassFields();
      this.validateFurnitureCategories();

      // Force revalidation after assignment - otherwise, fails and requires click into sc name.
      if (this.$refs.storeClassNamesForm?.validate) {
        await this.$refs.storeClassNamesForm.validate();
      }
    },

    revalidateStoreClassFields() {
      this.storeClasses.forEach(storeClass => {
        const fieldRef = this.$refs[`storeClassField_${storeClass._id}`];
        if (fieldRef && fieldRef[0]) {
          fieldRef[0].updateAndValidate(storeClass.name);
        }
      });
    },

    redrawRows() {
      mapValues(this.gridApis, v => v.gridApi.redrawRows());
    },

    hasFurniture(storeClassName) {
      const storeClass = this.storeClasses.find(sc => sc.name === storeClassName);
      if (!storeClass) return 'storeClassNotFound';
      if (storeClass.furnitureIds.length > 0) return true;

      return this.$t('validationErrors.needsFurniture', [this.tkey('storeClass')]);
    },

    isUniqueStoreClassName(storeClassName) {
      // since this page has a save button instead of inline editing, check length > 1.
      if (this.storeClasses.filter(sc => sc.name.trim() === storeClassName.trim()).length > 1) {
        return this.$t('validationErrors.unique', [this.tkey('storeClass')]);
      }
      return true;
    },

    addNewStoreClass(isSingleStoreClass = false) {
      this.storeClasses.push({
        name: isSingleStoreClass
          ? this.tkey('allFurnitureArchetype')
          : `${this.tkey('storeClass')} ${Math.floor(1000 + Math.random() * 9000)}`,
        // Give new storeClasses a temporary id so they can be used in the furniture arch mapping
        // server will give it a mongo ObjectId when persisted.
        _id: uniqueId(newlyAddedStoreClassIdPrefix),
        spacebreaks: [],
        furnitureIds: isSingleStoreClass ? map(this.getFurnituresInScenario, '_id') : [],
        generatedFurnitureIds: [],
        isEditingDisabled: false,
      });
      this.setupFurnituresWithStoreClasses();
      this.revalidateForm();
    },

    removeStoreClass(storeClassId) {
      this.storeClassToDelete = storeClassId;
      this.isDeleteDialogOpen = true;
      this.revalidateForm();
    },

    processCellFromClipboard(params) {
      const pasteFormatters = {
        storeClassId: value => {
          return get(this.storeClassByNameAndId, value, null);
        },
      };

      return agGridUtils.utils.processCellFromClipboard(params, pasteFormatters);
    },

    closeConfirmDialog() {
      this.isDeleteDialogOpen = false;
      this.isSingleStoreClassDialogOpen = false;
      this.storeClassToDelete = null;
      this.deleteAllStoreClasses = false;
    },

    setupFurnituresWithStoreClasses() {
      this.furnituresWithStoreClass = this.getFurnitureWithStoreClass();
      this.filterFurnitureWithStoreClass();
    },

    discardChanges() {
      this.nameKey = !this.nameKey; // force rtls-text-field to re-render with new prop value
      this.storeClasses = cloneDeep(this.getStoreClassesInScenario);
      this.setupFurnituresWithStoreClasses();
      this.revalidateForm();
    },

    handleFilterChange(filters) {
      this.filters = filters;
    },

    getFurnitureWithStoreClass() {
      return map(this.getFurnituresInScenario, f => {
        return {
          ...f,
          furnitureId: f._id,
          furnitureName: f.name,
          storeClassId: get(this.furnitureIdToStoreClassMap[f._id], '_id'),
        };
      });
    },

    filterFurnitureWithStoreClass() {
      this.filteredFurnituresWithStoreClass = filterUtils.applyFiltersToData({
        filters: this.filters,
        items: this.furnituresWithStoreClass,
      });
    },

    addFurnitureToStoreClass(storeClass, furnitureId) {
      const previousStoreClass = this.storeClasses.find(sc =>
        sc.furnitureIds.includes(furnitureId)
      );
      if (previousStoreClass) {
        previousStoreClass.furnitureIds = previousStoreClass.furnitureIds.filter(
          id => id !== furnitureId
        );
      }
      const targetStoreClass = this.storeClasses.find(sc => sc._id === storeClass._id);
      targetStoreClass.furnitureIds.push(furnitureId);
    },

    prepareStoreClassesForSave() {
      // Need to remove the temporary _ids as the server will create Mongo ObjectIds
      return this.storeClasses.map(storeClass => ({
        ...storeClass,
        spacebreaks: this.hasMappingChanges ? [] : storeClass.spacebreaks,
        _id: startsWith(storeClass._id, newlyAddedStoreClassIdPrefix) ? null : storeClass._id,
      }));
    },

    async saveChanges(commit = false) {
      if (!this.valid) return false;

      if (this.hasMappingChanges) {
        const results = await this.triggerDependencyTree({
          params: {
            change: 'storeClassModified',
            updates: {},
            commit,
          },
        });

        if (results.needsFeedback) {
          this.dependencyTreeFeedback = results.output;
          this.dependencyTreeModalOpen = true;
          return;
        }
      }

      await this.saveScenarioFurniture({ storeClasses: this.prepareStoreClassesForSave() });
      this.storeClasses = cloneDeep(this.getStoreClassesInScenario);
      this.setupFurnituresWithStoreClasses();
      await this.refreshScenario();
    },

    onSingleStoreClassClick() {
      this.deleteAllStoreClasses = true;

      // Only show the dialog if there are store classes that would be removed.
      if (this.storeClasses.length > 0) {
        this.isSingleStoreClassDialogOpen = true;
      } else {
        this.addNewStoreClass(true);
      }
    },

    replaceStoreClassToSingle() {
      this.closeConfirmDialog();
      this.storeClasses = [];
      this.addNewStoreClass(true);
    },

    onDeleteConfirm() {
      if (this.deleteAllStoreClasses) {
        this.replaceStoreClassToSingle();
      } else {
        this.storeClasses = this.storeClasses.filter(({ _id }) => _id !== this.storeClassToDelete);
      }

      this.closeConfirmDialog();
      this.setupFurnituresWithStoreClasses();
      this.revalidateForm();
      this.validateFurnitureCategories();
    },

    onGridReady(params, furnitureSet) {
      // need to track all ag-grids on page
      this.gridApis[furnitureSet] = { gridApi: params.api };
    },

    formatStoreclassName(params) {
      const storeClass = this.storeClassById[params.value];

      return get(storeClass, 'name', this.$t('general.select'));
    },

    onSelectionChange(params) {
      const { furnitureId } = params.data;
      const storeClassId = params.value;

      this.addFurnitureToStoreClass({ _id: storeClassId }, furnitureId);
      this.setupFurnituresWithStoreClasses();
      this.checkRowForErrors();
      this.revalidateForm();
    },

    modifySelectPosition(idSelector) {
      const agSelectList = document.querySelector(`${idSelector} .ag-select-list`);
      if (agSelectList) {
        const height = agSelectList.getBoundingClientRect().height;
        const bottom = agSelectList.getBoundingClientRect().bottom;
        const spaceBelow = window.innerHeight - bottom;
        // Find the distance between the dropdown select list and the bottom of the page, if lower than 100px then
        // reposition the select so it opens upwards rather than downwards, prevents increasing size of container
        if (spaceBelow < 100) {
          agSelectList.style.transform = `translateY(-${height}px)`;
        }
      }
    },

    checkRowForErrors() {
      // turn off filter if you've removed all invalid rows
      if (isEmpty(this.errorData) && this.filterInvalidRows) {
        this.toggleInvalidRows();
      }
    },

    doesExternalFilterPass(node) {
      if (this.filterInvalidRows) {
        return this.errorData[node.data._id];
      }
      return true;
    },

    toggleInvalidRows() {
      this.filterInvalidRows = !this.filterInvalidRows;
      Object.keys(this.gridApis).forEach(sc => {
        this.gridApis[sc].gridApi.onFilterChanged();
      });
    },

    checkMultipleRowsForErrors(rows) {
      each(rows, this.checkRowForErrors);
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },

    buildFurnitureAssignmentContext() {
      return {
        availableFurnitureArchetypes: this.furnituresWithStoreClass.map(f => ({
          furnitureName: f.furnitureName,
          furnitureArchetypeSet: f.furnitureCategory1Key,
          currentStoreClass: this.storeClassById[f.storeClassId]?.name || null,
        })),
        availableStoreClasses: this.storeClasses.map(sc => sc.name),
      };
    },

    buildInnerContext() {
      if (!this.selectedScenario) return {};

      const totalStoreClasses = this.storeClasses?.length || 0;
      const totalFurniture = this.furnituresWithStoreClass?.length || 0;
      const unassignedFurniture =
        this.furnituresWithStoreClass?.filter(f => !f.storeClassId).length || 0;
      const activeFilters = this.filters.length;

      const nameIssues =
        this.storeClasses?.filter(sc => {
          if (!sc.name || sc.name.trim() === '') return true;
          const duplicates = this.storeClasses.filter(other => other.name === sc.name);
          return duplicates.length > 1;
        }) || [];

      const validationErrors = [];
      if (unassignedFurniture > 0)
        validationErrors.push(`${unassignedFurniture} furniture without store class assignment`);
      if (nameIssues.length > 0)
        validationErrors.push('Store class names must be unique and non-empty');

      return {
        scenarioName: this.selectedScenario.name,
        scenarioProgress: this.formatScenarioProgress,
        totalStoreClasses,
        storeClasses:
          this.storeClasses?.map(sc => ({
            name: sc.name,
            furnitureCount: sc.furnitureIds?.length || 0,
          })) || [],
        totalFurniture,
        unassignedFurniture,
        activeFilters,
        filteredFurnitureCount:
          activeFilters > 0 ? this.filteredFurnituresWithStoreClass?.length || 0 : null,
        hasUnsavedChanges: this.hasDataChanges,
        showingOnlyInvalid: this.filterInvalidRows,
        validationErrors,
        canSave: validationErrors.length === 0,
        furnitureAssignments:
          this.furnituresWithStoreClass?.map(f => ({
            furnitureName: f.furnitureName,
            furnitureArchetypeSet: f.furnitureCategory1Key,
            storeClassName: this.storeClassById[f.storeClassId]?.name || null,
          })) || [],
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.assortment-table {
  height: 100%;
}

.list-title {
  padding: 0;
  width: 90%;
  margin: auto;
  margin-bottom: 2px;
  font-size: 1.2rem;
  margin-left: 17px;
}
.single-storeclass-btn {
  background-color: $assortment-background;
  color: $assortment-primary-colour;
  border: 1px solid $assortment-primary-colour;
}

.storeclass-col {
  padding: 2px;
}

.padded-column {
  padding-left: 5px;
}

.storeclass-border {
  &.v-divider {
    border-color: $assortment-menu-border-colour;
  }
}
.furniture-col-scrollable {
  position: relative;
  border-left: 1px solid $assortment-border-colour;
  border-top: 1px solid $assortment-border-colour;
  border-bottom: 1px solid $assortment-panel-border-divider-colour;
  -webkit-box-shadow: $assortment-main-panel-box-shadow;
  -moz-box-shadow: $assortment-main-panel-box-shadow;
  box-shadow: $assortment-main-panel-box-shadow;
  overflow-y: auto;
}

.dynamic-height-container {
  &.storeclass-container {
    border-bottom: none;
    height: $tab-panel-height;
    overflow: auto;
  }
}

.storeclass-list-container {
  display: flex;
  flex-grow: 1;
  overflow: auto;
}

// TODO simplify
.storeclass-table {
  margin-bottom: 1em;
  border-top: 1px solid $assortment-primary-colour;

  :deep(.ag-row:nth-child(odd)) {
    .v-input__slot,
    .v-select__slot {
      background: $assortment-input-background;
    }
  }
}

.storeclass-list-column {
  overflow-y: auto;
  border-bottom: 1px solid $assortment-panel-border-divider-colour;
  -webkit-box-shadow: inset 0 -10px 10px -10px rgba(0, 0, 0, 0.3);
  -moz-box-shadow: inset 0 -10px 10px -10px rgba(0, 0, 0, 0.3);
  box-shadow: inset 0 -10px 10px -10px rgba(0, 0, 0, 0.3);
}
.storeclass-list-scrollable {
  overflow-y: auto;
  .storeclass-card {
    background-color: $assortment-attribute-colour;
  }
}

.small-icon {
  height: 21px;
}

.info-icon {
  font-size: 20px;
  color: $assortment-primary-colour !important;
}

.filter-container {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-left: 30px;

  .filter-label {
    color: $assortment-text-colour;
    font-family: 'Source Sans Pro';
    font-size: 1.4rem;
    margin-right: 7px;
    letter-spacing: 0;
    line-height: 1.8rem;
  }
}

.storeclass-text-field {
  :deep(.v-text-field__slot) {
    input {
      font-weight: bold;
      background-color: $assortment-attribute-colour;
    }
  }
}

:deep(.tabled-select) {
  .v-select__selections {
    color: $assortment-table-textfield-colour !important;
    padding-left: 5px !important;
  }
}

.storeclass-btn {
  &.v-btn {
    min-width: 150px !important;
  }
}

:deep(.section) {
  font-weight: bolder;
  color: $assortment-table-header-colour;
  font-size: 1.4rem;
  background-color: white !important;
  padding: 5px 5px 5px 16px;
}

:deep(.ag-cell-value.ag-cell-not-inline-editing) {
  display: initial;
  text-overflow: ellipsis;
  overflow: hidden !important;
  white-space: nowrap;
}

:deep(.ag-cell) {
  overflow: visible;
}

:deep(.ag-row.ag-row-focus) {
  z-index: 1;
}
:deep(.ag-row.ag-row-no-focus.ag-row-level-0.ag-row-position-absolute) {
  z-index: 0;
}

:deep(.ag-root),
:deep(.ag-root-wrapper),
:deep(.ag-body-viewport),
:deep(.ag-body-viewport-wrapper),
:deep(.ag-center-cols-clipper),
:deep(.ag-center-cols-viewport) {
  overflow: visible !important;
}

:deep(.flex-grow-2) {
  flex-grow: 2;
}
</style>
